(() => {
  if (customElements.get('awa-installation2')) return;

  class AWAInstallation2 extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.abortController = new AbortController();
      this.busy = false;
      this.parentProduct = this.readJson('[data-installation2-parent]', null);
      this.serviceProducts = this.readJson('[data-installation2-services]', {});
      this.mountProducts = this.readJson('[data-installation2-mounts]', {});
      this.purchaseChoices = [...this.querySelectorAll('[data-installation2-purchase]')];
      this.options = this.querySelector('[data-installation2-options]');
      this.stateSelect = this.querySelector('[data-installation2-state]');
      this.coverageStatus = this.querySelector('[data-installation2-coverage-status]');
      this.serviceVariant = this.querySelector('[data-installation2-service-variant]');
      this.serviceVariantWrap = this.querySelector('[data-installation2-service-variant-wrap]');
      this.serviceTitle = this.querySelector('[data-installation2-service-title]');
      this.servicePrice = this.querySelector('[data-installation2-service-price]');
      this.serviceState = this.querySelector('[data-installation2-service-state]');
      this.summaryLines = this.querySelector('[data-installation2-summary-lines]');
      this.total = this.querySelector('[data-installation2-total]');
      this.addButton = this.querySelector('[data-installation2-add]');
      this.notice = this.querySelector('[data-installation2-notice]');
      this.postcodeInput = this.querySelector('[data-awa-installation-postcode]');
      this.productForm = document.getElementById(`product-form-${this.dataset.sectionId}`);
      this.sectionRoot = this.closest('.shopify-section') || this.closest('.product-section');
      this.mainProductFormHost = this.productForm?.closest('product-form');
      this.stickyProductForm = this.sectionRoot?.querySelector('product-add-to-cart-sticky') || null;
      this.hiddenElements = new Map();
      [this.mainProductFormHost, this.stickyProductForm].filter(Boolean).forEach((element) => {
        this.hiddenElements.set(element, element.hidden);
      });
      this.selectedVariants = new Map();

      if (!this.purchaseChoices.length || !this.options || !this.addButton) return;

      const signal = this.abortController.signal;
      this.addEventListener('change', (event) => this.handleChange(event), { signal });
      this.addButton.addEventListener('click', () => this.addSelections(), { signal });
      this.productForm?.addEventListener('change', () => this.render(), { signal });
      const quantity = this.productForm?.elements?.namedItem('quantity');
      quantity?.addEventListener('input', () => this.render(), { signal });
      quantity?.addEventListener('change', () => this.render(), { signal });
      this.postcodeInput?.addEventListener('input', () => this.render(), { signal });

      ['awa:postcode:ready', 'awa:postcode:changed'].forEach((eventName) => {
        window.addEventListener(eventName, (event) => {
          this.syncPostcode(event.detail);
          this.render();
        }, { signal });
      });
      window.addEventListener('awa:postcode:cleared', () => {
        this.postcodeState = '';
        this.render();
      }, { signal });
      window.addEventListener('awa:installation-coverage:changed', (event) => {
        if (String(event.detail?.productId) !== String(this.dataset.parentProductId)) return;
        this.coverageDetail = event.detail;
        this.syncPostcode(event.detail);
        this.render();
      }, { signal });
      document.addEventListener('awa:postcode:updated', (event) => {
        if (!event.detail?.productId || String(event.detail.productId) === String(this.dataset.parentProductId)) {
          this.syncPostcode(event.detail);
          this.render();
        }
      }, { signal });
      document.addEventListener('product:variant-change', (event) => {
        if (String(event.detail?.sectionId) === String(this.dataset.sectionId)) this.render();
      }, { signal });
      document.addEventListener('shopify:section:load', (event) => {
        if (event.target?.contains(this)) this.render();
      }, { signal });

      this.syncPostcode(this.sharedPostcode());
      this.render();
      requestAnimationFrame(() => {
        this.syncPostcode(this.sharedPostcode());
        this.render();
      });
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.hiddenElements?.forEach((wasHidden, element) => {
        element.hidden = wasHidden;
      });
      this.initialized = false;
    }

    readJson(selector, fallback, root = this) {
      try {
        return JSON.parse(root.querySelector(selector)?.textContent || JSON.stringify(fallback));
      } catch (_) {
        return fallback;
      }
    }

    formatMoney(cents) {
      if (typeof window.formatMoney === 'function') {
        return window.formatMoney(Number(cents) || 0, this.dataset.moneyFormat);
      }
      return new Intl.NumberFormat(document.documentElement.lang || 'en-AU', {
        style: 'currency',
        currency: this.dataset.currency || 'AUD'
      }).format((Number(cents) || 0) / 100);
    }

    availableVariant(product, preferredId) {
      if (!product || !Array.isArray(product.variants)) return null;
      const preferred = product.variants.find((variant) => String(variant.id) === String(preferredId) && variant.available);
      return preferred || product.variants.find((variant) => variant.available) || null;
    }

    purchaseMode() {
      return this.purchaseChoices.find((choice) => choice.checked)?.value || 'supply';
    }

    handleChange(event) {
      if (event.target.matches('[data-installation2-purchase]')) {
        if (event.target.value === 'supply') this.resetOptionalSelections();
      }
      if (event.target.matches('[data-installation2-service-variant]')) {
        this.selectedVariants.set('installation', String(event.target.value));
      }
      this.render();
    }

    resetOptionalSelections() {
      const noMount = this.querySelector('[data-installation2-mount][value=""]');
      const noRemoval = this.querySelector('[data-installation2-removal][value="no"]');
      const noElectrical = this.querySelector('[data-installation2-electrical][value="no"]');
      if (noMount) noMount.checked = true;
      if (noRemoval) noRemoval.checked = true;
      if (noElectrical) noElectrical.checked = true;
    }

    sharedPostcode() {
      try {
        return window.AWAPostcode?.get?.() || null;
      } catch (_) {
        return null;
      }
    }

    syncPostcode(detail) {
      const state = String(detail?.state || '').toUpperCase();
      this.postcodeState = state;
      if (state && this.stateSelect?.querySelector(`option[value="${state}"]`)) this.stateSelect.value = state;
    }

    selectedState() {
      return String(this.stateSelect?.value || '');
    }

    postcode() {
      return String(this.sharedPostcode()?.postcode || this.coverageDetail?.postcode || '').trim();
    }

    renderProduct(product, key, select, wrap, title, price, state) {
      const selected = this.availableVariant(product, this.selectedVariants.get(key) || select?.value);
      if (title) title.textContent = product?.title || '';
      if (price) price.textContent = selected ? this.formatMoney(selected.price) : '';
      if (state) {
        state.textContent = product && !selected
          ? (window.theme?.variantStrings?.soldOut || 'Sold out')
          : (!product ? this.dataset.errorConfiguration : '');
      }
      if (select) {
        const selectedId = selected ? String(selected.id) : '';
        select.replaceChildren();
        (product?.variants || []).forEach((variant) => {
          const option = document.createElement('option');
          option.value = String(variant.id);
          option.disabled = !variant.available;
          option.selected = String(variant.id) === selectedId;
          option.textContent = `${variant.title}${variant.available ? '' : ` — ${window.theme?.variantStrings?.soldOut || 'Sold out'}`}`;
          select.append(option);
        });
        if (wrap) wrap.hidden = (product?.variants?.length || 0) <= 1;
      }
      if (selected) this.selectedVariants.set(key, String(selected.id));
      else this.selectedVariants.delete(key);
      return selected ? { product, variant: selected, key } : null;
    }

    installationLine() {
      return this.renderProduct(
        this.serviceProducts.installation,
        'installation',
        this.serviceVariant,
        this.serviceVariantWrap,
        this.serviceTitle,
        this.servicePrice,
        this.serviceState
      );
    }

    selectedMount() {
      const choice = this.querySelector('[data-installation2-mount]:checked');
      const key = choice?.value || '';
      if (!key) return null;
      const product = this.mountProducts[key];
      const variant = this.availableVariant(product);
      if (!product || !variant) return null;
      return { product, variant, key: 'mount', mountKey: key };
    }

    selectedRemoval() {
      const choice = this.querySelector('[data-installation2-removal]:checked');
      if (choice?.value !== 'yes') return null;
      const product = this.serviceProducts.removal;
      const variant = this.availableVariant(product, this.selectedVariants.get('removal'));
      if (!product || !variant) return null;
      this.selectedVariants.set('removal', String(variant.id));
      return { product, variant, key: 'removal' };
    }

    selectedElectrical() {
      const choice = this.querySelector('[data-installation2-electrical]:checked');
      if (choice?.value !== 'yes') return null;
      const product = this.serviceProducts.electrical;
      const variant = this.availableVariant(product, this.selectedVariants.get('electrical'));
      if (!product || !variant) return null;
      this.selectedVariants.set('electrical', String(variant.id));
      return { product, variant, key: 'electrical' };
    }

    parentLine() {
      const id = String(this.productForm?.elements?.namedItem('id')?.value || this.parentProduct?.selected_or_first_available_variant?.id || '');
      const variant = this.parentProduct?.variants?.find((entry) => String(entry.id) === id && entry.available);
      return variant ? { product: this.parentProduct, variant, key: 'equipment' } : null;
    }

    quantity() {
      const control = this.productForm?.elements?.namedItem('quantity');
      const value = Math.floor(Number(control?.value || 1));
      return Number.isFinite(value) && value > 0 ? value : 1;
    }

    coverageResult(installation) {
      if (!installation) return { valid: false, reason: 'configuration' };
      const validator = window.AWAProductAvailability?.getInstallationValidation;
      if (typeof validator !== 'function') return { valid: false, reason: 'coverage_checker_inactive' };
      try {
        const result = validator(String(this.dataset.parentProductId), [{
          productId: String(installation.product.id),
          variantId: String(installation.variant.id),
          role: 'installation'
        }]) || { valid: false, reason: 'unknown' };
        if (result.reason === 'postcode_not_checked' || result.reason === 'coverage_checker_inactive') {
          return { ...result, valid: false };
        }
        return result;
      } catch (_) {
        return { valid: false, reason: 'coverage_check_failed', message: this.dataset.errorCoverage };
      }
    }

    locationResult(coverage) {
      const state = this.selectedState();
      const postcode = this.postcode();
      if (!state || !postcode) return { valid: false, reason: 'location_missing', message: this.dataset.errorLocation };
      if (this.postcodeInput && String(this.postcodeInput.value || '').trim() !== postcode) {
        return { valid: false, reason: 'postcode_unconfirmed', message: this.dataset.errorLocation };
      }
      if (this.postcodeState && state !== this.postcodeState) {
        return { valid: false, reason: 'state_mismatch', message: this.dataset.errorStateMismatch };
      }
      if (coverage.valid !== true) {
        return { valid: false, reason: coverage.reason, message: coverage.message || this.dataset.errorCoverage };
      }
      return { valid: true, reason: 'eligible' };
    }

    currentSelection() {
      const mode = this.purchaseMode();
      const parent = this.parentLine();
      if (mode !== 'installation') {
        return { mode, parent, quantity: this.quantity(), configured: Boolean(parent), coverage: { valid: true }, location: { valid: true } };
      }
      const installation = this.installationLine();
      const mount = this.selectedMount();
      const removal = this.selectedRemoval();
      const electrical = this.selectedElectrical();
      const coverage = this.coverageResult(installation);
      const location = this.locationResult(coverage);
      return {
        mode,
        parent,
        installation,
        mount,
        removal,
        electrical,
        quantity: this.quantity(),
        configured: Boolean(parent && installation),
        coverage,
        location
      };
    }

    syncPurchaseUI(mode) {
      const installationMode = mode === 'installation';
      this.options.hidden = !installationMode;
      this.purchaseChoices.forEach((choice) => {
        if (choice.value === 'installation') choice.setAttribute('aria-expanded', String(installationMode));
      });
      this.hiddenElements.forEach((wasHidden, element) => {
        element.hidden = installationMode ? true : wasHidden;
      });
    }

    renderCoverage(selection) {
      if (!this.coverageStatus) return;
      const available = selection.coverage.valid === true && selection.location.valid === true;
      const outsideCoverage = selection.coverage.reason === 'outside_coverage' || this.coverageDetail?.available === false;
      this.coverageStatus.classList.toggle('is-available', available);
      this.coverageStatus.classList.toggle('is-unavailable', outsideCoverage);
      if (available) this.coverageStatus.textContent = this.dataset.coverageAvailable;
      else if (outsideCoverage) this.coverageStatus.textContent = this.dataset.coverageUnavailable;
      else this.coverageStatus.textContent = selection.location.message || this.dataset.coveragePrompt;
    }

    renderSummary(selection) {
      this.summaryLines.replaceChildren();
      const lines = [selection.installation, selection.mount, selection.removal, selection.electrical].filter(Boolean);
      let total = 0;
      lines.forEach((line) => {
        total += (Number(line.variant.price) || 0) * selection.quantity;
        const item = document.createElement('li');
        const name = document.createElement('span');
        const price = document.createElement('span');
        const variantName = line.variant.title && line.variant.title !== 'Default Title' ? ` — ${line.variant.title}` : '';
        name.textContent = `${line.product.title}${variantName}`;
        price.className = 'awa-installation2__price price';
        price.textContent = this.formatMoney((Number(line.variant.price) || 0) * selection.quantity);
        item.append(name, price);
        this.summaryLines.append(item);
      });
      this.total.textContent = this.formatMoney(total);
    }

    render() {
      if (!this.initialized) return;
      const mode = this.purchaseMode();
      this.syncPurchaseUI(mode);
      if (mode !== 'installation') {
        this.selection = this.currentSelection();
        this.setNotice('');
        return;
      }
      const selection = this.currentSelection();
      this.selection = selection;
      this.renderCoverage(selection);
      this.renderSummary(selection);
      this.addButton.disabled = this.busy || !selection.configured || selection.location.valid !== true;
      this.addButton.setAttribute('aria-disabled', String(this.addButton.disabled));
      let error = '';
      if (!selection.parent) error = this.dataset.errorParent;
      else if (!selection.installation) error = this.dataset.errorConfiguration;
      else if (selection.location.valid !== true) error = selection.location.message || this.dataset.errorCoverage;
      if (!this.busy) this.setNotice(error, Boolean(error));
    }

    setNotice(message, error = false) {
      this.notice.textContent = message || '';
      this.notice.classList.toggle('is-error', Boolean(message && error));
      this.notice.hidden = !message;
    }

    packageKey() {
      const random = globalThis.crypto?.randomUUID?.() || Math.random().toString(36).slice(2);
      return `awa-${this.dataset.parentProductId}-${Date.now()}-${random}`;
    }

    cartItems(selection, packageKey = this.packageKey()) {
      const postcode = this.postcode();
      const state = this.selectedState();
      const quantity = selection.quantity || 1;
      const shared = {
        _awa_installation_package: packageKey,
        _awa_installation_equipment_product_id: String(this.dataset.parentProductId),
        'Installation postcode': postcode,
        'Installation state': state
      };
      const items = [{
        id: Number(selection.parent.variant.id),
        quantity,
        properties: {
          ...shared,
          _awa_installation_package_role: 'equipment',
          'Purchase type': 'Supply + Installation'
        }
      }, {
        id: Number(selection.installation.variant.id),
        quantity,
        properties: {
          ...shared,
          _awa_installation_package_role: 'installation',
          _awa_installation_service_role: 'installation'
        }
      }];
      if (selection.mount) {
        items.push({
          id: Number(selection.mount.variant.id),
          quantity,
          properties: {
            ...shared,
            _awa_installation_package_role: 'mount',
            _awa_installation_mount_type: selection.mount.mountKey
          }
        });
      }
      if (selection.removal) {
        items.push({
          id: Number(selection.removal.variant.id),
          quantity,
          properties: {
            ...shared,
            _awa_installation_package_role: 'removal',
            _awa_installation_service_role: 'removal'
          }
        });
      }
      if (selection.electrical) {
        items.push({
          id: Number(selection.electrical.variant.id),
          quantity,
          properties: {
            ...shared,
            _awa_installation_package_role: 'electrical',
            _awa_installation_service_role: 'electrical_circuit',
            'Electrical circuit': 'Add Circuit'
          }
        });
      }
      return items;
    }

    quantities(cart, ids) {
      const totals = new Map(ids.map((id) => [String(id), 0]));
      (cart?.items || []).forEach((item) => {
        const id = String(item.variant_id);
        if (totals.has(id)) totals.set(id, totals.get(id) + Number(item.quantity || 0));
      });
      return totals;
    }

    async readCart() {
      const response = await fetch(this.dataset.cartUrl, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!response.ok) throw new Error(this.dataset.errorGeneric);
      return response.json();
    }

    additionsConfirmed(before, after, items) {
      const expected = new Map();
      items.forEach((item) => expected.set(String(item.id), (expected.get(String(item.id)) || 0) + item.quantity));
      const ids = [...expected.keys()];
      const beforeTotals = this.quantities(before, ids);
      const afterTotals = this.quantities(after, ids);
      return ids.every((id) => afterTotals.get(id) - beforeTotals.get(id) >= expected.get(id));
    }

    sectionsToRender() {
      return [
        { id: 'Cart', section: 'main-cart', selector: '.thb-cart-form' },
        { id: 'Cart-Drawer', section: 'cart-drawer', selector: '.cart-drawer' },
        { id: 'cart-drawer-toggle', section: 'cart-bubble', selector: '.thb-item-count' }
      ];
    }

    renderCartSections(sections) {
      if (!sections) return false;
      let rendered = false;
      this.sectionsToRender().forEach((entry) => {
        const current = document.getElementById(entry.id);
        const html = sections[entry.section];
        if (!current || !html) return;
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const fresh = parsed.querySelector(entry.selector);
        const target = current.querySelector(entry.selector) || current;
        if (!fresh || !target) return;
        target.innerHTML = fresh.innerHTML;
        rendered = true;
      });
      try {
        if (typeof CartDrawer !== 'undefined') new CartDrawer();
      } catch (_) {}
      try {
        if (typeof Cart !== 'undefined') new Cart();
      } catch (_) {}
      return rendered;
    }

    async refreshCartSections() {
      const url = new URL(window.location.href);
      url.search = '';
      url.searchParams.set('sections', this.sectionsToRender().map((entry) => entry.section).join(','));
      const response = await fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (!response.ok) return false;
      return this.renderCartSections(await response.json());
    }

    openCartDrawer() {
      const drawer = document.getElementById('Cart-Drawer');
      if (!drawer || window.theme?.settings?.cart_drawer === false) return;
      document.body.classList.add('open-cc');
      drawer.classList.add('active');
      document.dispatchEvent(new CustomEvent('cart-drawer:open'));
    }

    async addSelections() {
      if (this.busy || this.purchaseMode() !== 'installation') return;
      if (this.productForm && !this.productForm.reportValidity()) return;
      this.render();
      const selection = this.selection;
      if (!selection?.configured || selection.location.valid !== true) return;
      const items = this.cartItems(selection);
      if (!items.length) return;

      this.busy = true;
      this.addButton.classList.add('loading');
      this.addButton.setAttribute('aria-busy', 'true');
      this.setNotice('');
      this.render();

      let before;
      let result;
      let added = false;
      let mutationStarted = false;
      try {
        before = await this.readCart();
        mutationStarted = true;
        const response = await fetch(this.dataset.cartAddUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            items,
            sections: this.sectionsToRender().map((entry) => entry.section),
            sections_url: window.location.pathname
          })
        });
        result = await response.json();
        if (!response.ok || result.status) {
          const error = new Error(result.description || result.message || this.dataset.errorGeneric);
          error.definitive = true;
          throw error;
        }
        added = true;
      } catch (error) {
        if (mutationStarted && !error.definitive && before) {
          try {
            const after = await this.readCart();
            added = this.additionsConfirmed(before, after, items);
          } catch (_) {
            added = false;
          }
        }
        if (!added) {
          const message = error.definitive
            ? error.message
            : (mutationStarted ? this.dataset.errorAmbiguous : this.dataset.errorGeneric);
          this.setNotice(message, true);
          return;
        }
      } finally {
        this.busy = false;
        this.addButton.classList.remove('loading');
        this.addButton.removeAttribute('aria-busy');
        this.render();
      }

      let refreshed = false;
      try {
        refreshed = this.renderCartSections(result?.sections);
      } catch (_) {
        refreshed = false;
      }
      if (!refreshed) {
        try {
          refreshed = await this.refreshCartSections();
        } catch (_) {
          refreshed = false;
        }
      }
      this.setNotice(refreshed ? this.dataset.addedMessage : `${this.dataset.addedMessage} ${this.dataset.refreshWarning}`);
      document.dispatchEvent(new CustomEvent('cart:item-added', { detail: { product: result?.items?.[0] || null } }));
      this.openCartDrawer();
    }
  }

  customElements.define('awa-installation2', AWAInstallation2);
})();
